/**
 * @file index.tsx
 *
 * @fileoverview Component for featured organizations.
 */
import { useQuery } from '@apollo/client';
import React, { useContext } from 'react';
import { OrganizationTeaser } from '~/jobiqo/components/Organization/Teaser';
import { FeaturedOrganizationsParagraph } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import { FormattedMessage } from 'react-intl';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import {
  getLandingPageOrganizationsVariables,
  GET_FEATURED_ORGANIZATIONS,
} from '~/jobiqo/components/Landingpage/Organizations/graphql';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { AddIcon } from '~/vcl/icons/Add';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { Badge } from '~/vcl/atoms/03-badge';
import NextLink from 'next/link';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const ShowAllOrganizationsButton = ({ frontendTheme }) => {
  const { ORGANIZATIONS } = useFrontendConfig();

  return (
    <Link
      href={ORGANIZATIONS.ORGANIZATIONS_SEARCH_PAGE}
      prefetch={false}
      asChild
    >
      <LinkButton
        linkElement={NextLink}
        buttonStyle={
          frontendTheme.LANDING_PAGES.ORGANIZATION_TEASERS.SHOW_ALL_BUTTON_STYLE
        }
        raised={false}
      >
        <AddIcon title="Show more" />
        <span>
          <FormattedMessage
            id="landing_companies_link"
            defaultMessage="See all organizations"
          />
        </span>
      </LinkButton>
    </Link>
  );
};

interface LandingPageOrganizationsProps {
  /**
   * Top intro text for display on the landing page row.
   */
  rowFeaturedOrgsInfo: FeaturedOrganizationsParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const LandingPageOrganizations = ({
  rowFeaturedOrgsInfo,
  maxContainerSize,
}: LandingPageOrganizationsProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const appContext: AppContextInterface = useContext(AppContext);
  const { error, loading, data } = useQuery(GET_FEATURED_ORGANIZATIONS, {
    variables: getLandingPageOrganizationsVariables(
      appContext,
      frontendConfig.ORGANIZATIONS,
      rowFeaturedOrgsInfo.industry?.tid,
    ),
  });

  const ParagraphInfo = ({ children }) =>
    frontendTheme.LANDING_PAGES.ORGANIZATION_TEASERS.INTRO_BADGE ? (
      <Badge>{children}</Badge>
    ) : (
      <Text
        className="jd-lp-org-teasers-intro"
        color={
          frontendTheme.LANDING_PAGES.ORGANIZATION_TEASERS.INTRO_COLOR ??
          'primary'
        }
        fontWeight="bold"
        textTransform="uppercase"
      >
        {children}
      </Text>
    );

  if (error) {
    // Do nothing.
    return null;
  }

  if (loading) {
    // Do nothing.
    return null;
  }

  if (data?.organizationProfiles?.pages?.length === 0) {
    // If we have no companies don't show the component.
    return null;
  }

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      padding={0}
      backgroundColorOverride={
        rowFeaturedOrgsInfo.background
          ? `var(--color-${rowFeaturedOrgsInfo.background})`
          : 'transparent'
      }
      data-test-id="featured_organizations"
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Spacing
          paddingTop={rowFeaturedOrgsInfo.spacing ?? 11}
          paddingBottom={rowFeaturedOrgsInfo.spacing ?? 12}
          margin={0}
        >
          <FlexItems justifyContent="space-between" alignItems="center">
            <FlexItem>
              {rowFeaturedOrgsInfo.introText && (
                <>
                  <ParagraphInfo>{rowFeaturedOrgsInfo.introText}</ParagraphInfo>
                  <Spacing size="medium" />
                </>
              )}
              {rowFeaturedOrgsInfo.introTextSecondary && (
                <>
                  <Text
                    tag="h2"
                    fontSize={[5, 6]}
                    lineHeight={[5, 6]}
                    {...(frontendTheme.LANDING_PAGES.JOB_TEASERS
                      .INTRO_SECONDARY_COLOR
                      ? {
                          color:
                            frontendTheme.LANDING_PAGES.JOB_TEASERS
                              .INTRO_SECONDARY_COLOR,
                        }
                      : null)}
                  >
                    {rowFeaturedOrgsInfo.introTextSecondary}
                  </Text>
                  <Spacing size="large" />
                </>
              )}
            </FlexItem>
            <HideMobile>
              <FlexItem justifySelf="flex-end">
                <ShowAllOrganizationsButton frontendTheme={frontendTheme} />
              </FlexItem>
            </HideMobile>
          </FlexItems>
          <Grid gap={6} columns={[1, 2, 4]}>
            {data?.organizationProfiles?.pages.length > 0 &&
              [...data?.organizationProfiles?.pages]
                .slice(0, 8)
                .map((organization) => (
                  <Cell
                    key={organization.id}
                    display="grid"
                    data-test-id="organization-teaser"
                  >
                    <OrganizationTeaser
                      key={organization.id}
                      organization={organization}
                    />
                  </Cell>
                ))}
          </Grid>
          <HideDesktop>
            <Spacing />
            <ShowAllOrganizationsButton frontendTheme={frontendTheme} />
          </HideDesktop>
        </Spacing>
      </Main>
    </Box>
  );
};
export default LandingPageOrganizations;
